import { UseCompaniesDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import buildUrl from 'modules/core/lib/buildUrl'
import { isFactorialIdUser } from 'modules/core/lib/factorialIdTokenRefresh'

import { useFactorialIdCompanies } from 'modules/factorial_id/hooks/useFactorialIdCompanies'

export interface Company {
  id: string
  name: string
  logo?: string
  factorial_id_id?: string
  external_id?: string
  cell?: string
}

interface UseCompaniesResult {
  companies: Company[]
  loading: boolean
}

export const useCompanies = (): UseCompaniesResult => {
  const { company: currentCompany, isAdminBecome, isStaffBecome } = useCurrentSession()

  const isFidAuthenticated = isFactorialIdUser()

  // Monolith companies list — used for non-FID sessions and as a fallback
  // source of the current company.
  const { data: monolithData, loading: monolithLoading } = useQuery(UseCompaniesDocument, {
    skip: isFidAuthenticated,
  })

  // Factorial ID company list
  const {
    data: fidCompanies,
    loading: fidLoading,
    error: fidError,
  } = useFactorialIdCompanies({ skip: !isFidAuthenticated })

  // Returning an empty list of companies can be dangerous because users of this
  // hook always expect at least one company, so in case of any errors we'll
  // return the current one.
  const getCurrentCompanyFallback = (): Company[] => [
    {
      id: currentCompany.id.toString(),
      name: currentCompany.name,
      logo: buildUrl(currentCompany.workspaceLogo),
    },
  ]

  if (isAdminBecome || isStaffBecome) {
    return { companies: getCurrentCompanyFallback(), loading: false }
  }

  if (isFidAuthenticated) {
    if (fidLoading) {
      return { companies: [], loading: true }
    }

    if (fidError || !fidCompanies?.length) {
      return { companies: getCurrentCompanyFallback(), loading: false }
    }

    return {
      companies: fidCompanies.map((fc) => ({
        id: fc.external_id,
        name: fc.name,
        logo: fc.full_logo_url,
        factorial_id_id: fc.id,
        external_id: fc.external_id,
        cell: fc.cell,
      })),
      loading: false,
    }
  } else {
    // Non-FID session: use the monolith companies list.
    if (monolithLoading) {
      return { companies: [], loading: true }
    }

    const monolithCompanies =
      monolithData?.companies.companiesConnection.nodes.map((c) => ({
        id: c.id.toString(),
        name: c.name,
        logo: buildUrl(c.workspaceLogo),
      })) ?? []

    if (!monolithCompanies.length) {
      return { companies: getCurrentCompanyFallback(), loading: false }
    }

    return {
      companies: monolithCompanies,
      loading: false,
    }
  }
}
